import React from 'react';
import { Row } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import Header from 'components/Header/Header';
import Footer from 'components/Footer/Footer';
import { Table } from 'components/Table';
import Help from 'components/Help/Help';
import Button from 'components/Button/Button';

import DetailedBidEntryModal from './components/DetailedBidEntryModal/DetailedBidEntryModal';
import useBidSheetState, { BidSheetRow } from './hooks/useBidSheetState';
import useColumns from './hooks/useColumns';

import './ProsforaBidSheet.scss';
import LinkButton from '../../components/LinkButton/LinkButton';
import { useSelector } from '../../store';
import Tooltip from '../../components/Tooltip/Tooltip';
import ErrorsTooltip from './components/ErrorsTooltip/ErrorsTooltip';
import BidsSummaryModal from './components/BidsSummaryModal/BidsSummaryModal';
import ColorLegend from './components/ColorLegend/ColorLegend';

export interface ProsforaBidSheetProps {}

// eslint-disable-next-line @typescript-eslint/naming-convention
const INITIAL_TABLE_STATE = {
  sorting: [
    {
      id: 'issuanceYear',
      desc: false,
    },
  ],
};

const getRowClassName = (row: Row<BidSheetRow>) => (row.original.selected ? 'selected' : '');

const ProsforaBidSheet = ({}: ProsforaBidSheetProps) => {
  const { t } = useTranslation();
  const { id: rfpId } = useSelector((state) => state.rfp.data);
  const [bidSheetState, bidActions] = useBidSheetState();
  const columns = useColumns(bidActions);

  const titleHelp = t('pages.bidSheet.titleHelp');

  return (
    <div className="enkrateia-prosfora-bid-sheet">
      <Header />
      <div className="content">
        <h2>
          <span>{t('pages.bidSheet.title')}</span>
          <Help>{titleHelp}</Help>
        </h2>
        <Table
          columns={columns}
          data={bidSheetState.rows}
          initialState={INITIAL_TABLE_STATE}
          getRowClassName={getRowClassName}
          onRowClick={bidActions.activateRow}
        />
        <div className="actions">
          <Tooltip
            tooltip={<ErrorsTooltip errors={bidSheetState.errors} />}
            variant="warn"
            disabled={bidSheetState.isValid}
          >
            <Button variant="dark-green-filled" disabled={!bidSheetState.isValid} onClick={bidActions.openSummaryModal}>
              {t('pages.bidSheet.confirmBid', { count: bidSheetState.selectedRows.length })}
            </Button>
          </Tooltip>
          <Button variant="secondary" onClick={bidActions.reset}>
            {t('pages.bidSheet.clearEntries')}
          </Button>
          <LinkButton variant="black" to={`/rfp/${rfpId}`}>
            {t('pages.bidSheet.cancel')}
          </LinkButton>
        </div>
        <ColorLegend />
        <DetailedBidEntryModal
          bid={bidSheetState.activeRow}
          saveBid={bidActions.updateRow}
          onRequestClose={bidActions.deactivateRow}
        />
        <BidsSummaryModal
          isOpen={bidSheetState.summaryModalOpen}
          onRequestClose={bidActions.closeSummaryModal}
          selectedRows={bidSheetState.selectedRows}
        />
      </div>
      <Footer />
    </div>
  );
};

export default ProsforaBidSheet;
